"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { rijderHref, favorietSectie, type SectieCode } from "@/lib/secties";
import { FavorietKnop } from "@/components/favoriet-knop";
import { Afkorting } from "@/components/afkorting";
import type { UitslagSectie, UitslagRij } from "@/server/uitslagen";
import { beslissendeRit, type Resultaat, type Ritten } from "@/lib/afstand";
import type { DeelnemerSoort } from "@/server/wedstrijden";

const SOORT_LABEL: Record<DeelnemerSoort, string> = {
  competitie: "Competitie",
  eurocup: "Euro Cup",
  overig: "Overig",
};
const SOORT_VOLGORDE: DeelnemerSoort[] = ["competitie", "eurocup", "overig"];

function soortenIn(sectie: UitslagSectie): DeelnemerSoort[] {
  return SOORT_VOLGORDE.filter((s) => sectie.klassen.some((k) => k.deelnemers.some((d) => d.soort === s)));
}
export function WedstrijdUitslag({ secties }: { secties: UitslagSectie[] }) {
  const [sectieIdx, setSectieIdx] = useState(0);
  const sectie = secties[sectieIdx] ?? secties[0];
  const soorten = sectie ? soortenIn(sectie) : [];
  const [soort, setSoort] = useState<DeelnemerSoort>(soorten[0] ?? "competitie");
  // Alles dichtgeklapt beginnen: een open klasse met tientallen rijders duwt de
  // volgende blokken ver onder de vouw, waardoor het lijkt alsof die niet opengaan.
  const [openKlasse, setOpenKlasse] = useState<string | null>(null);

  if (!sectie) return null;

  const zichtbaar = sectie.klassen
    .map((k) => ({ ...k, deelnemers: k.deelnemers.filter((d) => d.soort === soort) }))
    .filter((k) => k.deelnemers.length > 0);

  function kiesSectie(i: number) {
    const s = secties[i];
    const so = soortenIn(s);
    setSectieIdx(i);
    setSoort(so[0] ?? "competitie");
    setOpenKlasse(null);
  }
  function kiesSoort(s: DeelnemerSoort) {
    setSoort(s);
    setOpenKlasse(null);
  }

  return (
    <div className="py-5">
      {secties.length > 1 && (
        <div className="mb-4 flex gap-2 overflow-x-auto px-4 pb-1">
          {secties.map((s, i) => (
            <button
              key={s.code}
              type="button"
              onClick={() => kiesSectie(i)}
              className={cn(
                "shrink-0 rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors",
                i === sectieIdx ? "bg-primary text-primary-foreground" : "bg-card text-white/70 hover:text-white",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {soorten.length > 1 && (
        <div className="mb-4 flex gap-2 px-4">
          {soorten.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => kiesSoort(s)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ring-1 transition-colors",
                s === soort
                  ? "text-primary ring-primary"
                  : "text-white/50 ring-white/15 hover:text-white/80",
              )}
            >
              {SOORT_LABEL[s]}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3 px-4">
        {zichtbaar.map((k) => {
          const open = openKlasse === k.key;
          return (
            <div key={k.key} className="overflow-hidden rounded-xl border border-border bg-card">
              <button
                type="button"
                onClick={() => setOpenKlasse(open ? null : k.key)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
              >
                <span className="min-w-0 truncate font-display text-base font-bold uppercase tracking-wide text-white">
                  {k.naam}
                </span>
                <span className="flex shrink-0 items-center gap-2.5">
                  <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-bold tabular-nums text-primary">
                    {k.deelnemers.length}
                  </span>
                  <ChevronDown
                    className={cn(
                      "size-5 text-primary transition-transform duration-200",
                      open && "rotate-180",
                    )}
                    aria-hidden
                  />
                </span>
              </button>

              {open && (
                <ul className="divide-y divide-white/5 border-t border-white/5">
                  {k.deelnemers.map((d, i) => (
                    <li key={i}>
                      <UitslagItem
                        d={d}
                        href={rijderHref(sectie.code, d.deelnID)}
                        sectie={sectie.code}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function UitslagItem({
  d,
  href,
  sectie,
}: {
  d: UitslagRij;
  href: string | null;
  sectie: SectieCode;
}) {
  // Wie is uitgevallen of niet gestart heeft geen klassering. TRS zet zulke
  // rijders allemaal op dezelfde stand - bij Oud Gastel stonden er vijf op 12 -
  // wat eruitziet als een fout. NTTO laat het nummer op ntto.nl/live weg, dus
  // dat doen wij ook: een streepje houdt de rijen uitgelijnd.
  const uitslag = beslissendeRit(d.ritten);
  const geklasseerd = uitslag != null && uitslag.soort !== "status";
  const podium = geklasseerd && d.positie <= 3;

  const inhoud = (
    <>
      <div
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-lg font-display text-base font-black tabular-nums",
          !geklasseerd
            ? "bg-muted text-white/25"
            : podium
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-primary",
        )}
      >
        {geklasseerd ? d.positie : "–"}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-white">{d.titel}</p>
        {d.ondertitel && <p className="truncate text-xs text-muted-foreground">{d.ondertitel}</p>}
      </div>
    </>
  );

  // Ook de uitslag staat naast de link en niet erin, om dezelfde reden als de
  // ster hieronder: een afkorting als DIS is aan te tikken voor uitleg (C5), en
  // dan moet je niet tegelijk naar het profiel springen.
  const resultaat = (
    <div className="shrink-0 py-2.5 pl-3">
      <ResultaatKolom ritten={d.ritten} />
    </div>
  );

  // De ster staat naast de link, niet erin: een knop in een link is geen geldige
  // opmaak, en je zou bij het volgen doorklikken naar het profiel.
  const ster =
    d.deelnID != null ? (
      <FavorietKnop
        sectie={favorietSectie(sectie)}
        id={d.deelnID}
        naam={d.titel}
        lijst
        smal
      />
    ) : null;

  if (!href) {
    return (
      <div className="flex items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-3">{inhoud}</div>
        {resultaat}
        {ster}
      </div>
    );
  }
  return (
    <div className="flex items-center">
      <Link
        href={href}
        className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-3 transition-colors active:bg-white/5"
      >
        {inhoud}
      </Link>
      {resultaat}
      {ster}
    </div>
  );
}

// Rechterkolom van een uitslagrij.
//
// Iedereen rijdt een kwalificatierit; alleen de besten komen terug voor de
// finale, en die rit bepaalt de einduitslag. Bij een finalist tonen we dus
// twee uitslagen, onder elkaar en niet naast elkaar: de rij heeft links toch
// al twee regels (tractor en chauffeur), dus dit kost geen extra hoogte -
// naast elkaar zou op een telefoon niet passen.
//
// De labels Kwal. en Finale zijn dezelfde die NTTO op ntto.nl/live gebruikt,
// zodat bezoekers niet twee woordenlijsten hoeven te leren.
function ResultaatKolom({ ritten }: { ritten: Ritten }) {
  const { kwalificatie, toelichting, slot, slotLabel } = ritten;
  if (!kwalificatie && !slot) return null;

  // Zonder afsluitende rit is er maar één uitslag; die staat alleen, zonder label.
  if (!slot) {
    return (
      <span className="shrink-0 text-right leading-tight">
        <ResultaatTekst r={kwalificatie!} />
        <Toelichting tekst={toelichting} />
      </span>
    );
  }

  return (
    <span className="shrink-0 text-right leading-tight">
      <span className="flex items-baseline justify-end gap-1.5">
        <span className="text-[0.6rem] font-semibold uppercase tracking-wide text-primary">
          {slotLabel}
        </span>
        <ResultaatTekst r={slot} />
      </span>
      {kwalificatie && (
        <span className="flex items-baseline justify-end gap-1.5 text-muted-foreground">
          <span className="text-[0.6rem] font-semibold uppercase tracking-wide">Kwal.</span>
          <span className="text-xs">{kwalificatie.tekst}</span>
        </span>
      )}
      <Toelichting tekst={toelichting} />
    </span>
  );
}

// Kleine regel onder de uitslag. Bij een herkansing staat hier welke poging
// verviel: die telt namelijk niet meer mee, ook als hij beter was. Zonder deze
// regel ziet iemand die bij de baan een hoger getal zag alleen het lagere.
function Toelichting({ tekst }: { tekst: string | null }) {
  if (!tekst) return null;
  return (
    <span className="block whitespace-nowrap text-[0.6rem] text-muted-foreground">{tekst}</span>
  );
}

function ResultaatTekst({ r }: { r: Resultaat }) {
  if (r.soort === "status") {
    // Aantikbaar, niet in een title: dat is een muis-tooltip en op een
    // telefoon gebeurde er niets. Zie afkorting.tsx.
    return <Afkorting tekst={r.tekst} uitleg={r.uitleg} className="text-sm font-bold text-muted-foreground" />;
  }
  return <span className="text-sm font-bold text-white">{r.tekst}</span>;
}
